import { useRef, useState } from 'react';
import type React from 'react';


import { Box } from 'ui/box';

import { BarChart } from 'components/partials/charts';
import { BarChartHorizontal } from 'components/partials/charts/bar-chart/bar-chart-horizontal';
import { validateBarChartProps } from 'components/partials/charts/shared/chart-validation';
import {
  CHART_TABLET_MIN_WIDTH_PX,
  CHART_DIMENSIONS,
} from 'components/partials/charts/shared/chart-tokens';
import type { ChartLabelFormat } from 'components/partials/charts/shared/types';
import { useElementWidthAtLeast } from 'hooks/use-element-width-at-least';
import { useMediaQuery } from 'hooks/use-media-query';
import { useChartExport } from 'hooks/use-chart-export';
import {
  decideBarChartOrientation,
  resolvePlotMaxWidth,
  shouldForceVerticalChartFrameMaxWidth,
  valueLabelMinContainerWidthPx,
} from './chart-layout';
import ChartPartFrame from './chart-part-frame';
import type { ChartViewMode } from './chart-part-frame';
import ChartPartTable from './chart-part-table';
import MessageInfoItem from '../message-info-item';

type MarkChartType = 'BAR';
type MarkFormat = 'NUMBER' | 'PERCENT' | 'CURRENCY';

interface MarkBarChartData {
  chartType?: unknown;
  title?: unknown;
  subtitle?: unknown;
  format?: unknown;
  categoryAxis?: {
    label?: unknown;
    categories?: unknown;
  } | null;
  series?: ReadonlyArray<{
    label?: unknown;
    values?: unknown;
  } | null> | null;
}

export interface ChartPartProps {
  data: unknown;
  cacheId?: string;
}

const FORMAT_MAP: Record<MarkFormat, ChartLabelFormat> = {
  NUMBER: 'number',
  PERCENT: 'percent',
  CURRENCY: 'currency',
};

const INVALID_CHART_MESSAGE =
  'This chart could not be displayed. Try asking your question again.';
// Complements the tablet min-width so one viewport cannot match both queries.
const SUB_PIXEL_OFFSET = 0.02;
const MOBILE_VIEWPORT_QUERY = `(max-width: ${
  CHART_TABLET_MIN_WIDTH_PX - SUB_PIXEL_OFFSET
}px)`;

const getChartFormat = (format: unknown): ChartLabelFormat => {
  if (typeof format !== 'string') return 'number';

  return FORMAT_MAP[format as MarkFormat] ?? 'number';
};

const isBarChartType = (chartType: unknown): chartType is MarkChartType =>
  chartType === 'BAR';

const asBarChartPayload = (data: unknown): MarkBarChartData | null => {
  if (!data || typeof data !== 'object') return null;

  return data as MarkBarChartData;
};

const buildTableCaption = (title?: string, subtitle?: string): string => {
  const descriptor = [title, subtitle].filter(Boolean).join(' - ');

  return descriptor ? `Data table for ${descriptor}` : 'Data table for chart';
};

const ChartPart = ({ data, cacheId }: ChartPartProps): React.JSX.Element => {
  const [manualViewMode, setManualViewMode] = useState<ChartViewMode | null>(
    null
  );
  const isMobileViewport = useMediaQuery(MOBILE_VIEWPORT_QUERY);
  const chartAreaRef = useRef<HTMLDivElement>(null);

  const payload = asBarChartPayload(data);
  const validation =
    payload && isBarChartType(payload.chartType)
      ? validateBarChartProps({
          categoryAxis: payload.categoryAxis,
          series: payload.series,
          format: getChartFormat(payload.format),
        })
      : null;
  const normalized = validation?.valid ? validation.normalized : null;

  const title = typeof payload?.title === 'string' ? payload.title : undefined;
  const subtitle =
    typeof payload?.subtitle === 'string' ? payload.subtitle : undefined;

  const { exportChart, isExporting } = useChartExport({
    targetRef: chartAreaRef,
    data: normalized,
    fileName: title,
  });

  const categoryCount = normalized?.categoryAxis.categories.length ?? 0;
  const seriesCount = normalized?.series.length ?? 0;
  const { ref: chartContainerRef, isAtLeast: showValueLabels } =
    useElementWidthAtLeast(
      valueLabelMinContainerWidthPx(categoryCount, seriesCount)
    );

  if (!normalized) {
    return <MessageInfoItem message={INVALID_CHART_MESSAGE} role="img" />;
  }

  const tableCaption = buildTableCaption(title, subtitle);

  const defaultOrientation = decideBarChartOrientation(
    isMobileViewport,
    categoryCount,
    seriesCount
  );
  const viewMode: ChartViewMode = manualViewMode ?? defaultOrientation;
  const plotMaxWidthPx = resolvePlotMaxWidth(categoryCount, seriesCount);
  const isVerticalMode = viewMode === 'vertical';
  const isHorizontalMode = viewMode === 'horizontal';
  const isTableMode = viewMode === 'table';
  const shouldForceMaxWidth =
    isTableMode ||
    (isVerticalMode && shouldForceVerticalChartFrameMaxWidth(plotMaxWidthPx));
  const chartFadeKey = isHorizontalMode ? 'horizontal-chart' : 'vertical-chart';

  return (
    <ChartPartFrame
      title={title}
      subtitle={subtitle}
      viewMode={viewMode}
      onViewModeChange={setManualViewMode}
      preferredContentWidthPx={isVerticalMode ? plotMaxWidthPx : undefined}
      forceMaxWidth={shouldForceMaxWidth}
      chartAreaRef={chartAreaRef}
      onExport={exportChart}
      isExporting={isExporting}
    >
      <Box ref={chartContainerRef} sx={{ minWidth: 0, width: '100%' }}>
        {viewMode === 'table' ? (
          <div key="table" className="animate-fade-in">
            <ChartPartTable data={normalized} caption={tableCaption} />
          </div>
        ) : (
          <div
            key={chartFadeKey}
            className="animate-fade-in w-full mr-auto"
            style={
              isVerticalMode ? { maxWidth: `${plotMaxWidthPx}px` } : undefined
            }
          >
            {isHorizontalMode ? (
              <BarChartHorizontal data={normalized} animate={false} />
            ) : (
              <BarChart
                data={normalized}
                maxBarSize={CHART_DIMENSIONS.maxBarSizePx}
                animate={false}
                showValueLabels={showValueLabels}
                categoryLabelMode="auto"
                cacheId={cacheId}
              />
            )}
          </div>
        )}
      </Box>
    </ChartPartFrame>
  );
};

export default ChartPart;
